import { Direction_Enum } from '@weave/schema-gen-ts/dist/schemas/fax/shared/v1/enums.pb';
import { Contact, FaxRecipient } from '@weave/schema-gen-ts/dist/schemas/fax/v1/fax.pb';
import { Bool_Enum } from '@weave/schema-gen-ts/dist/shared/null/types.pb';
import { QueryFunctionContext, UseMutationOptions, UseQueryOptions, useInfiniteQuery, useQuery } from 'react-query';
import { useMutation, ContextlessQueryObserverOptions } from '@frontend/react-query-helpers';
import { HTTPBoundSchemaMethod, SchemaFaxService, SchemaIO } from '@frontend/schema';
import { getContactMedia, sendFax } from './api';
import { FaxTypes } from '.';

const REQUEST_LIMIT = 25;

type SchemaMutationOptions<T extends HTTPBoundSchemaMethod<any>> = UseMutationOptions<
  SchemaIO<T>['output'],
  unknown,
  SchemaIO<T>['input'],
  unknown
>;

const defaultOptions: ContextlessQueryObserverOptions = {
  refetchOnMount: true,
  refetchOnWindowFocus: false,
};

export const queryKeys = {
  base: ['fax'] as const,
  listFaxes: ({
    type,
    locationId,
    locationIds,
    filters,
  }: {
    type: string;
    locationId: string;
    locationIds?: string[];
    filters?: {
      isViewed?: Bool_Enum;
      isDelivered?: Bool_Enum;
      direction?: Direction_Enum;
      tags?: string[];
      locationPhones?: string[];
    };
  }) => [...queryKeys.base, 'list', 'faxes', { type, locationId, locationIds, filters }],
  listContacts: ({ type, locationId, locationIds }: { type: string; locationId: string; locationIds: string[] }) => [
    ...queryKeys.base,
    'list',
    'contacts',
    { type, locationId, locationIds },
  ],
  searchContacts: ({ query, locationId, limit }: { query: string; locationId: string; limit?: number }) => [
    ...queryKeys.base,
    'search',
    'contacts',
    { query, locationId, ...(limit ? { limit } : {}) },
  ],
  getFaxMedia: (mediaId: string, pageNumber: number | undefined, locationId: string) => [
    ...queryKeys.base,
    'media',
    mediaId,
    pageNumber,
    locationId,
  ],
  listDefaultFaxNumber: (locationId: string) => [...queryKeys.base, 'default-fax-number', locationId],
  listNumbers: ([locationIds]: string[]) => [...queryKeys.base, 'numbers', { locationIds }],
  getDefaultFaxNumber: () => [...queryKeys.base, 'default-fax-number'],
  getFaxContact: (id: string) => [...queryKeys.base, 'contact', id],
  countFaxes: (locationIds: string[]) => [...queryKeys.base, 'count', { locationIds }],
  getFaxContactMedia: (mediaId: string, locationId: string) => [
    ...queryKeys.base,
    'contact-media',
    mediaId,
    locationId,
  ],
  getFaxContacts: (locationId: string, limit: number, locationIds?: string[]) => [
    ...queryKeys.base,
    'list',
    'contacts',
    { locationId, locationIds, limit },
  ],
  forwardFax: () => [...queryKeys.base, 'forward'],
  getFax: (id: string) => [...queryKeys.base, 'fax', id],
  getFaxForUpdate: (id: string) => [...queryKeys.base, 'fax', 'faxUpdate', id],
};

type SchemaListFaxes = SchemaIO<(typeof SchemaFaxService)['ListFaxes']>;
export const listFaxes = (req: SchemaListFaxes['input']) => SchemaFaxService.ListFaxes(req);

type SchemaListContacts = SchemaIO<(typeof SchemaFaxService)['ListContacts']>;
export const listContacts = (req: SchemaListContacts['input']) => SchemaFaxService.ListContacts(req);

type SchemaListBlockedFaxes = SchemaIO<(typeof SchemaFaxService)['ListBlockedFaxes']>;
export const listBlockedFaxes = (req: SchemaListBlockedFaxes['input']) => SchemaFaxService.ListBlockedFaxes(req);

type SchemaListArchivedFaxes = SchemaIO<(typeof SchemaFaxService)['ListArchivedFaxes']>;
export const listArchivedFaxes = (req: SchemaListArchivedFaxes['input']) => SchemaFaxService.ListArchivedFaxes(req);

type SchemaSearchContacts = SchemaIO<(typeof SchemaFaxService)['SearchContacts']>;
export const searchContacts = (req: SchemaSearchContacts['input']) => SchemaFaxService.SearchContacts(req);

type SchemaSearchFaxes = SchemaIO<(typeof SchemaFaxService)['SearchFaxes']>;
export const searchFaxes = (req: SchemaSearchFaxes['input']) => SchemaFaxService.SearchFaxes(req);

type ListContactsPageParams =
  | {
      cursor?: string;
    }
  | undefined;

type useListContactsProps = {
  locationId: string;
  locationIds: string[];
  type: FaxTypes.InboxType;
  enabled: boolean;
};

export const useQueryFaxContacts = (
  {
    locationId,
    locationIds,
    limit,
    recentContacts,
  }: {
    locationId: string;
    locationIds?: string[];
    limit: number;
    recentContacts?: boolean;
  },
  opts: UseQueryOptions<SchemaIO<(typeof SchemaFaxService)['ListContacts']>['output']['contacts']> = {}
) => {
  return useQuery({
    queryKey: queryKeys.getFaxContacts(locationId, limit, locationIds),
    queryFn: () =>
      SchemaFaxService.ListContacts({
        locationId,
        locationIds,
        limit,
        includeAnonymousContacts: true,
        recentContacts: !!recentContacts,
      }).then((res) => res.contacts),
    ...opts,
  });
};

export const useQueryFaxNumbers = (
  locationIds: string[],
  opts: UseQueryOptions<SchemaIO<(typeof SchemaFaxService)['ListNumbers']>['output']['locationPhones']> = {}
) => {
  return useQuery({
    queryKey: queryKeys.listNumbers(locationIds),
    queryFn: () =>
      SchemaFaxService.ListNumbers({
        locationId: locationIds[0],
        ...(locationIds[1] && { locationIds }),
      }).then((res) => res.locationPhones),
    enabled: !!locationIds?.length && opts.enabled !== false,
    ...opts,
  });
};

export const useQueryDefaultFaxNumber = (
  locationId: string,
  opts: UseQueryOptions<SchemaIO<(typeof SchemaFaxService)['ListNumbers']>['output']['defaultLocationPhone']> = {}
) => {
  return useQuery({
    queryKey: queryKeys.listDefaultFaxNumber(locationId),
    queryFn: () => SchemaFaxService.ListNumbers({ locationId }).then((res) => res.defaultLocationPhone),
    ...opts,
  });
};

type SendDraftReq = { draftId: string; locationId: string; locationPhone: string; faxRecipients: FaxRecipient[] };
export const useSendFax = (opts: UseMutationOptions<unknown, unknown, SendDraftReq> = {}) => {
  return useMutation({
    ...opts,
    mutationFn: ({ draftId, locationId, locationPhone, faxRecipients }) =>
      sendFax(draftId, locationId, locationPhone, faxRecipients),
  });
};

export const useForwardFax = (opts: SchemaMutationOptions<(typeof SchemaFaxService)['ForwardFax']> = {}) =>
  useMutation({
    mutationKey: queryKeys.forwardFax(),
    mutationFn: SchemaFaxService.ForwardFax,
    ...opts,
  });

export const useListContactsInfiniteQuery = ({ locationId, locationIds, type, enabled }: useListContactsProps) => {
  return useInfiniteQuery({
    queryKey: queryKeys.listContacts({ type, locationId, locationIds }),
    queryFn: async ({ pageParam }: QueryFunctionContext<string | readonly unknown[], ListContactsPageParams>) => {
      const res = await listContacts({
        locationId,
        locationIds,
        cursor: pageParam?.cursor ?? '',
      });

      return {
        rows: res.contacts ?? [],
        cursor: res.cursor ?? '',
      };
    },
    getNextPageParam: (lastGroup: {
      rows: (string | Contact | undefined)[];
      cursor: string;
    }): ListContactsPageParams => {
      if (!lastGroup?.rows?.length) return undefined;
      if (!lastGroup.cursor) return undefined;
      return {
        cursor: lastGroup.cursor,
      };
    },
    ...defaultOptions,
    enabled,
  });
};

type useSearchContactsProps = {
  locationId: string;
  locationIds?: string[];
  query: string;
  enabled: boolean;
  limit?: number;
};

type SearchContactsPageParams =
  | {
      offset: number;
    }
  | undefined;

export const useSearchContactsInfiniteQuery = ({
  locationId,
  locationIds,
  query,
  enabled,
  limit,
}: useSearchContactsProps) => {
  return useInfiniteQuery({
    queryKey: queryKeys.searchContacts({ query, locationId, limit: limit || REQUEST_LIMIT }),
    queryFn: async ({ pageParam }: QueryFunctionContext<string | readonly unknown[], SearchContactsPageParams>) => {
      const res = await searchContacts({
        locationId,
        locationIds,
        query,
        offset: pageParam?.offset ?? 0,
        limit: limit || REQUEST_LIMIT,
      });

      return {
        rows: res.contacts ?? [],
      };
    },
    getNextPageParam: (lastGroup: { rows: (string | Contact | undefined)[] }): SearchContactsPageParams => {
      if (lastGroup.rows.length < REQUEST_LIMIT) return undefined;
      const lastContact = lastGroup?.rows[lastGroup?.rows.length - 1];
      if (!lastContact) return undefined;
      return {
        // BE will be updating this prop, right now it will always be 0
        offset: 0,
      };
    },
    ...defaultOptions,
    enabled,
  });
};

type UpsertContactRes = SchemaIO<(typeof SchemaFaxService)['UpsertContact']>['output'];
type UpsertContactReq = SchemaIO<(typeof SchemaFaxService)['UpsertContact']>['input'];
export const useUpsertFaxContact = (opts: UseMutationOptions<UpsertContactRes, unknown, UpsertContactReq> = {}) => {
  return useMutation({
    mutationFn: (contact: UpsertContactReq) => {
      return SchemaFaxService.UpsertContact(contact);
    },
    ...opts,
  });
};

type GetContactRes = SchemaIO<(typeof SchemaFaxService)['GetContact']>['output'];
export const useGetFaxContact = (contactId: string, locationId: string, opts: UseQueryOptions<GetContactRes> = {}) => {
  return useQuery({
    queryKey: queryKeys.getFaxContact(contactId),
    queryFn: () => SchemaFaxService.GetContact({ contactId, locationId }),
    ...opts,
  });
};

type CountFaxesRes = SchemaIO<(typeof SchemaFaxService)['CountFaxes']>['output'];
export const useCountFaxes = (locationIds: string[], opts: UseQueryOptions<CountFaxesRes> = {}) => {
  return useQuery({
    queryKey: queryKeys.countFaxes(locationIds),
    queryFn: () => SchemaFaxService.CountFaxes({ locationIds, isRead: Bool_Enum.FALSE, capCount: true }),
    ...opts,
  });
};

export const useGetFaxContactMedia = (mediaId: string, locationId: string, opts: UseQueryOptions<Blob> = {}) => {
  return useQuery({
    queryKey: queryKeys.getFaxContactMedia(mediaId, locationId),
    queryFn: () => getContactMedia(mediaId, locationId),
    ...opts,
  });
};

type GetFaxRes = SchemaIO<(typeof SchemaFaxService)['GetFax']>['output'];
export const useGetFax = (faxId: string, locationId: string, opts: UseQueryOptions<GetFaxRes> = {}) => {
  return useQuery({
    queryKey: queryKeys.getFax(faxId),
    queryFn: () => SchemaFaxService.GetFax({ faxId, locationId }),
    ...opts,
  });
};
